import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { uniq } from 'lodash';
import { GetOperatorInvestorListQuery, UserLegalEntity } from 'src/generated/graphql-types';
import {
  AdminUserType,
  EmailsIdMapping,
  Entity,
  InvestmentUnpublished,
  InvestorUnpublished,
  Offering,
  Operator,
  OperatorInvestor,
  PrimaryAndSecondaryEmails,
  SavingError,
} from 'src/types';
import { uniqueArrayOfObjects } from 'src/utils/functions/d.utils';
import { RootState } from '../config/rootReducer';

type CustomPreferenceQuestion = {
  id: string;
  question: string;
  answers: {
    key: string;
    description: string;
  }[];
};

type OperatorInvestorListValue =
  GetOperatorInvestorListQuery['findOperatorInvestorsList'][number] & {
    name: string;
    operatorId: string;
  };

type initialStateType = {
  operator: any;
  isEditMode: boolean;
  viewType: string;
  media: any;
  mediaInvestors: any;
  operatorEntities: any[];
  fullyLoadedOperatorEntitiesIDs: string[]; // If present, operator project won't be loaded from the server again
  selectedEntity: any;
  investorsList: Array<OperatorInvestorListValue>;
  investorsListLoading: boolean;
  filteredInvestorsList: OperatorInvestor[] | SavingError;
  investorsFromInvestmentsList: any[];
  investments: any[];
  isNeedRefresh: boolean;
  saving: boolean;
  savingError: any;
  deletedInvestors: any[];
  deletedInvestments: any[];
  navigationDict: any;
  offerings: any;
  logoFile: any;
  customPreferenceQuestions: CustomPreferenceQuestion[];
  activeInvestmentStep: { [k: string]: number }; // [project-id] : stepNumber
  selectedPendingRequest: string;
  admins: AdminUserType[];
  overviewKpisCategories: string[];
};

const initialState: initialStateType = {
  operator: null,
  isEditMode: true,
  viewType: 'achieved',
  media: null,
  mediaInvestors: null,
  operatorEntities: [],
  fullyLoadedOperatorEntitiesIDs: [],
  selectedEntity: null,
  investorsList: [],
  investorsListLoading: false,
  filteredInvestorsList: [],
  investorsFromInvestmentsList: [],
  investments: [],
  isNeedRefresh: false,
  saving: false,
  savingError: null,
  deletedInvestors: [],
  deletedInvestments: [],
  navigationDict: null,
  offerings: null,
  logoFile: undefined, //Init as undefine to spot the difference between no file and not loaded
  customPreferenceQuestions: [],
  activeInvestmentStep: {},
  selectedPendingRequest: '',
  admins: [],
  overviewKpisCategories: ['commitments', 'investments', 'distributions', 'openCommitments'],
};

const operator = createSlice({
  name: 'operator',
  initialState,
  reducers: {
    setOperator(state, { payload }: PayloadAction<Operator>) {
      state['operator'] = payload as Operator;
    },
    setOperatorEntities(state, { payload }: PayloadAction<Entity[]>) {
      state['operatorEntities'] = payload as Entity[];
    },
    setFullyLoadedOperatorEntitiesIDs(state, { payload }: PayloadAction<string[]>) {
      state['fullyLoadedOperatorEntitiesIDs'] = payload;
    },
    setSelectedEntity(state, { payload }: PayloadAction<Entity>) {
      state['selectedEntity'] = payload;
    },
    setEditableMode(state, { payload }: PayloadAction<boolean>) {
      state['isEditMode'] = payload;
    },
    setViewType(state, { payload }: PayloadAction<'achieved' | 'profimex'>) {
      state['viewType'] = payload;
    },
    setMedia(state, { payload }: PayloadAction<any[]>) {
      state['media'] = payload;
    },
    setMediaInvestors(state, { payload }: PayloadAction<any>) {
      state['mediaInvestors'] = payload;
    },
    setInvestorsList(state, { payload }: PayloadAction<OperatorInvestorListValue[]>) {
      state['investorsList'] = payload;
    },
    setInvestorsListLoading(state, { payload }: PayloadAction<boolean>) {
      state['investorsListLoading'] = payload;
    },
    setFilteredInvestorsList(state, { payload }: PayloadAction<any>) {
      state['filteredInvestorsList'] = payload;
    },
    setInvestorsFromInvestmentsList(state, { payload }: PayloadAction<any>) {
      state['investorsFromInvestmentsList'] = payload;
    },
    setInvestments(state, { payload }: PayloadAction<InvestmentUnpublished[]>) {
      state['investments'] = payload;
    },
    setIsNeedRefresh(state, { payload }: PayloadAction<boolean>) {
      state['isNeedRefresh'] = payload;
    },
    setSaving(state, { payload }: PayloadAction<boolean>) {
      state['saving'] = payload;
    },
    setSavingError(state, { payload }: PayloadAction<SavingError | null>) {
      state['savingError'] = payload;
    },
    setDeletedInvestors(state, { payload }: PayloadAction<InvestorUnpublished[]>) {
      state['deletedInvestors'] = payload;
    },
    setDeletedInvestments(state, { payload }: PayloadAction<InvestmentUnpublished[]>) {
      state['deletedInvestments'] = payload;
    },
    setNavigationDict(state, { payload }: PayloadAction<any>) {
      state['navigationDict'] = payload;
    },
    updateNavigationDictById(state, { payload }: PayloadAction<{ id: string; value: string }>) {
      state['navigationDict'][payload.id] = payload.value;
    },
    setOfferings(state, { payload }: PayloadAction<Offering[]>) {
      state['offerings'] = payload as Offering[];
    },
    setLogoFile(state, { payload }: PayloadAction<any>) {
      state['logoFile'] = payload;
    },
    updateOffering(state, { payload }: PayloadAction<Offering>) {
      state['offerings'] = payload;
    },
    setCustomPreferenceQuestions(state, { payload }: PayloadAction<CustomPreferenceQuestion[]>) {
      state['customPreferenceQuestions'] = payload;
    },
    setActiveInvestmentStep(state, { payload }: PayloadAction<any>) {
      state['activeInvestmentStep'] = payload;
    },
    setSelectedPendingRequest(state, { payload }: PayloadAction<string>) {
      state['selectedPendingRequest'] = payload;
    },
    setAdmins(state, { payload }: PayloadAction<AdminUserType[]>) {
      state['admins'] = payload;
    },
    setOverviewKpisCategories(state, { payload }: PayloadAction<string[]>) {
      state['overviewKpisCategories'] = payload;
    },
  },
});

const selectInvestorsList = (state: RootState) => state.operator.investorsList;

// define computed data
/**
 * List of investors, where key is investor email and value is a list of investor's legal entity names
 */
export const selectInvestedByList: (state: RootState) => Record<string, string[]> = createSelector(
  selectInvestorsList,
  investorsList => {
    return investorsList.reduce((acc, investor) => {
      const investorInvestedByList =
        [
          ...new Set(investor?.userLegalEntities?.map(entity => entity?.name.toLowerCase().trim())),
        ] || [];
      return { ...acc, [investor.email]: investorInvestedByList };
    }, {});
  },
);

/**
 * Gets all user legal entities related to the current operator
 */
export const getAllUserLegalEntities: (state: RootState) => UserLegalEntity[] = createSelector(
  selectInvestorsList,
  investorsList => {
    //map all entities
    const allMapping = investorsList
      ?.map(investor => investor.userLegalEntities?.map(entity => entity))
      .flat();

    //remove nulls
    const allMappingValid = allMapping.filter(valid => valid);
    return uniqueArrayOfObjects(allMappingValid, 'id'); //return unique array
  },
);

/**
 * Creates primary and secondary collection of emails, example: {key: primaryEmail, value: [secondary1, secondary2]}
 */
export const selectAllInvestorsEmails: (state: RootState) => PrimaryAndSecondaryEmails =
  createSelector(selectInvestorsList, investorsList => {
    //init collection
    let emails: PrimaryAndSecondaryEmails = {};
    investorsList.forEach(investor => {
      const primaryEmail = investor.email;
      // aka viewer emails from all legal entities in which current investor is assigned as investor (not viewer)
      const secondaryEmails = investor.userLegalEntities
        .filter(entity => entity.investorIDs.includes(investor.id))
        .map(entity => entity.viewers.map(viewer => viewer.email))
        .flat();
      const uniqueSecondaryEmails = uniq(secondaryEmails);

      if (!emails[primaryEmail]) {
        emails[primaryEmail] = uniqueSecondaryEmails;
      } else {
        emails[primaryEmail] = uniq([...emails[primaryEmail], ...uniqueSecondaryEmails]);
      }
    });

    return emails;
  });

/**
 * Creates primary and secondary collection of emails, example: {key: primaryEmail, value: [secondary1, secondary2]}
 */
export const selectAllEmailsByIds: (state: RootState) => EmailsIdMapping = createSelector(
  selectInvestorsList,
  investorsList => {
    //init collection
    let emails: EmailsIdMapping = {};

    //loop on all legal entities
    investorsList.forEach(investor => {
      investor.userLegalEntities.forEach(entity => {
        //add investors email mapping
        entity.investors.forEach(
          investorEntity => (emails[investorEntity.id] = investorEntity.email),
        );

        //add viewers email mapping
        entity.viewers.forEach(viewerEntity => (emails[viewerEntity.id] = viewerEntity.email));
      });
    });

    return emails;
  },
);

export const {
  setOperator,
  setOperatorEntities,
  setFullyLoadedOperatorEntitiesIDs,
  setSelectedEntity,
  setEditableMode,
  setViewType,
  setMedia,
  setInvestorsList,
  setInvestorsListLoading,
  setFilteredInvestorsList,
  setInvestorsFromInvestmentsList,
  setMediaInvestors,
  setIsNeedRefresh,
  setInvestments,
  setSaving,
  setSavingError,
  setDeletedInvestors,
  setDeletedInvestments,
  setNavigationDict,
  updateNavigationDictById,
  setOfferings,
  updateOffering,
  setLogoFile,
  setCustomPreferenceQuestions,
  setActiveInvestmentStep,
  setSelectedPendingRequest,
  setAdmins,
  setOverviewKpisCategories,
} = operator.actions;

export default operator.reducer;
